// File-based routing implementation
import { PUBLIC_ROUTE_COMPONENTS } from '../config/routes.js'
import { featureFlagService } from '../api/featureFlagService.js'
import { bundleLoader } from '../services/bundleLoader.js'
import { locale } from '../services/localeService.js'

// Global route state
let appInstance = null
let currentComponent = 'home-page'
let currentQueryParams = {}
let storeInstance = null
let localeServiceInstance = null

// Unsaved changes tracking - allows components to register a callback
let hasUnsavedChangesCallback = null
let isConfirmingNavigation = false

// Many pages call the native `history.replaceState(null, ...)` directly for
// their own in-page sub-tab/view URL syncing (mode toggles, tab params, etc.)
// - that's fine and unrelated to routing, but passing `null` blows away
// whatever state object is already on the current entry, including the
// `__navVisited` marker markBackNavigationIfTraversal() (below) relies on for
// back/forward detection. Patch replaceState once, centrally, so it always
// carries that marker forward regardless of what any other call site passes,
// rather than auditing/touching every such call site across the app.
if (typeof window !== 'undefined' && window.history && !window.history.__navReplaceStatePatched) {
    const nativeReplaceState = window.history.replaceState.bind(window.history)
    window.history.replaceState = function (state, title, url) {
        const hadMarker = !!(window.history.state && window.history.state.__navVisited)
        const mergedState = (state && typeof state === 'object')
            ? (hadMarker ? { __navVisited: true, ...state } : state)
            : (hadMarker ? { __navVisited: true } : state)
        return nativeReplaceState(mergedState, title, url)
    }
    window.history.__navReplaceStatePatched = true
}

// Dynamic import to avoid circular dependency - only loaded when needed
async function loadStore() {
  if (!storeInstance) {
    storeInstance = (await import('../services/store.js')).store
  }
  return storeInstance
}

// Dynamic import for locale service - cached after first load
async function loadLocaleService() {
  if (!localeServiceInstance) {
    const { getLocaleService } = await import('../services/service-loader.js')
    localeServiceInstance = await getLocaleService()
  }
  return localeServiceInstance
}

// Public routes that don't require authentication (imported from centralized config)
const PUBLIC_ROUTES = PUBLIC_ROUTE_COMPONENTS

// Feature flag protected routes
// Maps component names to their required feature flags
const FEATURE_FLAG_ROUTES = {
    'billing-page': 'billing_ui_page',
    'credits-page': 'credit_bundle_purchase',
    // Add more protected routes as needed
}

// Role-based route access as defense-in-depth.
//
// The primary control in production is Lambda@Edge (see
// constructs/GatewayConstruct/lambdas/jwt-validator.ts) which refuses to
// serve /assets/system-admin/* and pageLoader.bundle.system-admin.js to
// anyone who isn't a SYSTEM_ADMIN. Dev/mock environments don't have that,
// so this map is the guard that keeps a CUSTOMER_ADMIN from navigating
// to e.g. #audit-logs just by typing the URL.
//
// Enforcement: if the user's role isn't in the allow-list for the route,
// we redirect to #home instead of loading the page. Unlisted routes are
// not role-restricted (public + user bundle routes live elsewhere).
const ROLE_ROUTES = {
    'admin-page': ['SYSTEM_ADMIN'],
    'customers-page': ['SYSTEM_ADMIN'],
    'customer-deletions-page': ['SYSTEM_ADMIN'],
    // METALARK-671 — new-customer wizard and metrics dashboard.
    'new-customer-page': ['SYSTEM_ADMIN'],
    'metrics-page': ['SYSTEM_ADMIN'],
    'campaigns-page': ['SYSTEM_ADMIN'],
    'custom-domains-page': ['SYSTEM_ADMIN'],
    'audit-logs-page': ['SYSTEM_ADMIN'],
    'feature-flags-page': ['SYSTEM_ADMIN'],
    'users-page': ['SYSTEM_ADMIN'],
    'email-templates-page': ['SYSTEM_ADMIN'],
    'email-suppression-list-page': ['SYSTEM_ADMIN'],
    'notification-dashboard-page': ['SYSTEM_ADMIN'],
    'quota-health-page': ['SYSTEM_ADMIN'],
    'xray-page': ['SYSTEM_ADMIN'],
    'activity-dashboard-page': ['SYSTEM_ADMIN'],
    // METALARK-671 — Stripe setup status + reconcile (SSM params, partner-bus rules).
    'stripe-setup-page': ['SYSTEM_ADMIN'],
    // METALARK-913 — Repository provider OAuth setup status (SSM client creds).
    'repository-providers-setup-page': ['SYSTEM_ADMIN'],
}

// Page title mapping - keys reference locale strings
const PAGE_TITLE_KEYS = {
    'home-page': 'pagetitle_home',
    'login-page': 'pagetitle_login',
    'select-workspace-page': 'pagetitle_selectworkspace',
    'settings-page': 'pagetitle_settings',
    'skills-page': 'pagetitle_skills',
    'skill-page': 'pagetitle_skilldetails',
    'content-page': 'pagetitle_content',
    'collection-page': 'pagetitle_collection',
    'reports-page': 'pagetitle_reports',
    'report-preview-page': 'pagetitle_report',
    'export-page': 'pagetitle_export',
    'upload-page': 'pagetitle_upload',
    'summary-page': 'pagetitle_summary',
    'item-page': 'pagetitle_item',
    'deep-search-page': 'pagetitle_deepsearch',
    'verify-email-page': 'pagetitle_verifyemail',
    'reset-password-page': 'pagetitle_resetpassword',
    'forgot-password-page': 'pagetitle_forgotpassword',
    'trial-signup-page': 'pagetitle_trialsignup',
    'trial-success-page': 'pagetitle_trialcreated',
    'users-page': 'pagetitle_users',
    'customers-page': 'pagetitle_customers',
    'customer-360-page': 'pagetitle_customer360',
    'new-customer-page': 'admin_new_customer_title',
    'metrics-page': 'admin_metrics_title',
    'campaigns-page': 'admin_campaigns_title',
    'audit-logs-page': 'pagetitle_auditlogs',
    'email-logs-page': 'pagetitle_emaillogs',
    'email-templates-page': 'pagetitle_emailtemplates',
    'email-suppression-list-page': 'pagetitle_emailsuppressionlist',
    'billing-page': 'pagetitle_billingsubscription',
    'credits-page': 'pagetitle_purchasecredits',
    'not-found-page': 'pagetitle_notfound',
    'support-release-notes-page': 'pagetitle_releasenotes',
    'support-lms-integration-page': 'pagetitle_lmsintegration',
    'support-user-guide-page': 'pagetitle_userguide',
    'support-external-player-page': 'pagetitle_externalplayer',
    'support-lms-portability-page': 'pagetitle_lmsportability',
    'customer-tags-page': 'pagetitle_customertags',
    'customer-skills-page': 'pagetitle_customerskills',
    'computed-attribute-convention-page': 'pagetitle_computedattributeconvention',
    'migration-page': 'pagetitle_migration',
    'bulk-edit-page': 'pagetitle_bulkedit',
    'bulk-apply-page': 'pagetitle_bulkapply',
}

export const BASE_TITLE = 'MetaLark'

// Set app instance for updates
export function setAppInstance(instance) {
    appInstance = instance
}

// Parse query parameters from hash
export function parseQueryParams(hashString) {
    const params = {}
    if (hashString && hashString.includes('?')) {
        const queryString = hashString.split('?')[1]
        const searchParams = new URLSearchParams(queryString)
        for (const [key, value] of searchParams) {
            params[key] = value
        }
    }
    console.log('[Router] parseQueryParams - input:', hashString, 'parsed params:', params);
    return params
}

// Helper function to generate title from component name
function generateTitleFromComponentName(componentName) {
    // Remove '-page' suffix and split on hyphens
    const name = componentName.replace('-page', '')
    return name
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

// Update page title based on route
async function updatePageTitle(componentName, queryParams = {}) {
     // Load locale service for translations (cached after first load)
     let locale = null
     try {
         locale = await loadLocaleService()
     } catch (error) {
         console.warn('[Router] Could not load locale service for page title:', error)
     }

     // Get translation key or fallback to generated title
     const titleKey = PAGE_TITLE_KEYS[componentName]
     const pageTitle = titleKey && locale ? locale.t(titleKey) : generateTitleFromComponentName(componentName)

     // Get customer name from Redux store if available
     let customerName = null
     try {
         const store = await loadStore()
         const state = store.getState()
         customerName = state.customer?.name
     } catch (error) {
         console.warn('[Router] Could not load customer name for title:', error)
     }

    // Build title parts
    const titleParts = [pageTitle]

    // Add query param context if present
    if (queryParams.reason) {
        const reasonTitles = {
            'session-expired': locale ? locale.t('pagetitle_sessionexpired') : 'Session Expired',
            'logged-out': locale ? locale.t('pagetitle_loggedout') : 'Logged Out'
        }
        const reasonText = reasonTitles[queryParams.reason] || queryParams.reason
        titleParts[0] = `${pageTitle} (${reasonText})`
    }

    // Add customer name if available (helps distinguish tabs)
    if (customerName) {
        titleParts.push(customerName)
    }

    // Always end with base title
    titleParts.push(BASE_TITLE)

    document.title = titleParts.join(' - ')
}

// Update current component and trigger re-render
async function updateCurrentComponent(componentName, queryParams = {}) {
     console.log('[Router] updateCurrentComponent - componentName:', componentName, 'queryParams:', queryParams);
     currentComponent = componentName
     currentQueryParams = queryParams

     // Update page title
     await updatePageTitle(componentName, queryParams)

    if (appInstance) {
        console.log('[Router] Updating app instance with queryParams:', queryParams);
        appInstance.update({
            currentComponent: componentName,
            currentQueryParams: queryParams
        })
    }

    // METALARK-632: emit a PAGE_VIEW activity event after the route is
    // committed. Fire-and-forget — the analytics endpoint failing must
    // never block navigation. trackPageView contains its own guards
    // (auth, slice context, dedupe, public routes) so this stays a
    // single line at the call site.
    trackPageView(componentName, queryParams)
}

// METALARK-632: page-view tracking. Backend's BillingAndAnalyticsMiddleware
// can't auto-categorize PAGE_VIEW (the SPA never hits API routes for a page
// transition — every API path begins with /api/v1/...), so we emit one
// explicitly per route change here. Goes through the same
// /api/v1/dashboard/activity/track endpoint the LMS launch flow uses.

// Stable session id for the lifetime of this browser tab. Persisted in
// sessionStorage so a hard reload keeps the same session, but a fresh tab
// gets its own. Distinct from the backend's deterministic sub+iat hash —
// frontend events and middleware-tracked API calls won't share a sessionId,
// but that's an acceptable gap for now and can be tightened later by
// reading the JWT (currently httpOnly so unreadable from JS).
function getOrCreateClientSessionId() {
    if (typeof sessionStorage === 'undefined') return null
    try {
        let sid = sessionStorage.getItem('analytics_session_id')
        if (!sid) {
            // crypto.randomUUID is in every browser we ship to; ts the
            // first-paint timestamp into the fallback so manual debugging
            // can still distinguish tabs even if randomUUID isn't there.
            sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID()
                : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
            sessionStorage.setItem('analytics_session_id', sid)
        }
        return sid
    } catch {
        return null
    }
}

// Identity query params — these distinguish "different views of the same
// route" from each other. Cross-record navigation (customer A vs B,
// resource X vs Y) and major within-page section changes (Billing tab
// vs Users tab on customer-360) get fresh page_view events; trivial UI
// state (sort, filter, page, search) stays dedup'd.
//
// `tab` is included so the analytics dashboard can see which sections
// of a page admins are spending time in. Other UI-state params (sort,
// filter, page) are intentionally NOT here because changing them
// doesn't represent a meaningful navigation — the user is still on
// the same view, just resorting it.
const IDENTITY_QUERY_PARAMS = ['customerId', 'resourceId', 'skillId', 'collectionId', 'userId', 'sliceId', 'tab']

function buildDedupeKey(componentName, queryParams) {
    const identityValues = IDENTITY_QUERY_PARAMS
        .map(k => queryParams && queryParams[k] != null ? `${k}=${queryParams[k]}` : null)
        .filter(Boolean)
        .join('&')
    return identityValues ? `${componentName}?${identityValues}` : componentName
}

// Module-level dedupe guard. Re-firing on every hashchange — including
// trivial UI-state changes (tab=billing → tab=usage) and back/forward
// re-mounts that resolve to the same view — would inflate event volume
// without adding signal. Dedupe on (componentName + identity params)
// captures distinct page views while silencing within-page UI churn.
let lastTrackedDedupeKey = null

// ────────────────────────────────────────────────────────────────────
// In-app navigation event ('inapp-routechange')
// ────────────────────────────────────────────────────────────────────
//
// `window.history.pushState(...)` updates the URL silently — the
// browser does NOT emit a `hashchange` event. So a page that uses
// pushState to change identity-level query params (e.g. customer-360
// switching tabs via `?tab=...`) won't trigger the router's pageview
// tracker bound to `hashchange`.
//
// The accepted pattern in this codebase is to dispatch a custom
// `inapp-routechange` CustomEvent after the pushState write. The
// pageview tracker listens for both `hashchange` (real navigation,
// browser back/forward) AND `inapp-routechange` (in-page identity
// changes), so trackPageView fires on either path.
//
// Choosing this over synthesizing a hashchange has three benefits:
//   1. Only the pageview tracker reacts — auth/bundle/redirect logic
//      in handleRouteChange isn't re-run for a same-page tab click.
//   2. Other `hashchange` listeners (login-form, content-nav-bar,
//      future ones) don't get phantom fires on tab clicks.
//   3. Listeners can opt in selectively. The audit/email-log filter
//      URLs use the same custom-event pattern (with their own event
//      names) for in-page filter updates — see auditLogFilterUrl.js
//      and emailLogFilterUrl.js.
//
// To use from a page that does pushState:
//   window.history.pushState(null, '', newHash);
//   window.dispatchEvent(new CustomEvent('inapp-routechange'));
//
// The handler reads the current URL itself (no payload needed).
const INAPP_ROUTE_CHANGE_EVENT = 'inapp-routechange';
function handleInappRouteChange() {
    const hash = window.location.hash || '';
    const componentName = getComponentNameFromHash(hash);
    const queryParams = parseQueryParams(hash);
    trackPageView(componentName, queryParams);
}

async function trackPageView(componentName, queryParams = {}) {
    const dedupeKey = buildDedupeKey(componentName, queryParams)

    // Same view as last fire — within-page tab/sort/filter changes
    // (and back/forward re-mounts) are not new page views.
    if (dedupeKey === lastTrackedDedupeKey) return

    // Public routes (login, verify-email, reset-password, trial-signup,
    // etc.) are pre-auth and don't have a queryContext at all — backend
    // would 401. Skip cleanly. Same allow-list the auth gate uses above.
    if (PUBLIC_ROUTES.includes(componentName)) return

    let userContext
    try {
        const { tools } = await import('../services/tools.js')
        userContext = await tools.getUserContext()
    } catch (error) {
        // No user context resolvable — first paint before /auth/me lands.
        // Skip silently; the next handleRouteChange after auth resolves
        // will fire instead.
        return
    }

    // Initial bootstrap: /auth/me hasn't returned yet. Skip — the next
    // navigation after auth lands fires instead.
    //
    // METALARK-632: we no longer require customerId/sliceId on the
    // userContext. The backend service substitutes a 'system' sentinel
    // when those are absent (Aurora DSQL doesn't support DROP NOT NULL
    // on those columns), so admin-tier navigation across customers and
    // unscoped admin pages now generates page_view events too.
    //
    // Field-name note: /auth/me's response (MeResponse) uses `userUuid`,
    // not `userId`. The guest fallback in tools.js sets `userId: null`,
    // hence the field-name asymmetry. An authenticated user has
    // `userUuid` set; a guest doesn't. Using `userUuid` here both means
    // we don't fire for guests *and* covers the prior bug where a
    // mistyped `userContext.userId` short-circuited every authenticated
    // navigation (the field doesn't exist on the authenticated shape,
    // so it was always falsy → always returned).
    if (!userContext || !userContext.userUuid) return

    const sessionId = getOrCreateClientSessionId()
    if (!sessionId) return

    // Mark BEFORE the await so two near-simultaneous hashchange events
    // (e.g. mobile gesture replay) don't both win the dedupe race and
    // double-fire.
    lastTrackedDedupeKey = dedupeKey

    try {
        const { getDashboardActivityService } = await import('../services/service-loader.js')
        const svc = await getDashboardActivityService()
        // activityTarget is the route name (no leading hash, no query) so
        // dashboards can group "all views of the audit-logs page" without
        // string-parsing every variant of the URL. Real identity params
        // ride in activityData.queryParams so analytics can still resolve
        // "who viewed customer X" without round-tripping the URL.
        const activityTarget = componentName.replace(/-page$/, '')
        await svc.trackActivity(
            sessionId,
            'page_view',
            activityTarget,
            Object.keys(queryParams).length > 0 ? { queryParams } : undefined,
        )
    } catch (error) {
        // Fire-and-forget. A failed track is not a user-visible problem,
        // and we already redirected the page; warn loudly so we notice in
        // dev but never propagate.
        console.warn('[Router] trackPageView failed:', error)
        // Reset so a retry can fire on the next navigation if it was
        // transient (network blip, brief 401 during token refresh).
        if (lastTrackedDedupeKey === dedupeKey) {
            lastTrackedDedupeKey = null
        }
    }
}

// Helper function to get component name from hash
function getComponentNameFromHash(hash) {
    // Handle empty or root paths
    if (!hash || hash === '' || hash === '#' || hash === '#/') {
        return 'home-page'
    }
    
    // Remove leading # and any query parameters
    const routeName1 = hash.replace('#', '').split('?')[0];
    const routeName = routeName1.split('/').pop();

    // Default to home if no hash or empty hash
    if (!routeName || routeName === '') {
        return 'home-page'
    }

    // Handle special case for home
    if (routeName === 'home') {
        return 'home-page'
    }

    // Add '-page' suffix to match component tag names
    return `${routeName}-page`
}

window.hashHistory = [window.location.hash || "#home"];

// Register a callback to check for unsaved changes
export function registerUnsavedChangesCheck(callback) {
    hasUnsavedChangesCallback = callback
}

// Clear the unsaved changes check (called after save or when component unmounts)
export function clearUnsavedChangesCheck() {
    hasUnsavedChangesCallback = null
}

// Show confirmation dialog for unsaved changes
async function showUnsavedChangesDialog() {
    return new Promise((resolve) => {
        const message = locale.t('unsaved_changes_warning')
        const result = confirm(message)
        resolve(result) // true if user confirms, false if user cancels
    })
}

// Handle route changes with authentication guard and unsaved changes check
// Detect a genuine back/forward-button traversal by whether the browser is
// restoring a history entry we've already stamped, rather than relying on
// `popstate` - some browsers/automation contexts fire it unreliably (zero,
// multiple, or delayed times) for what is really an ordinary forward
// hash-only navigation, which made popstate-based detection intermittently
// misclassify a forward nav-tab click as a back press. A brand-new entry
// (from navigateTo()'s hash write, a raw <a href> click, or a
// replaceState-based in-app update) always starts with `history.state ===
// null`; only a real traversal restores an entry's own previously-stamped
// state, so this works regardless of whether/when popstate fires.
let hasCheckedInitialRoute = false
function markBackNavigationIfTraversal() {
    // The very first route resolution after a full page (re)load is deliberately
    // NOT checked here - index.tag's pageshowHandler (Performance Navigation
    // Timing + bfcache) already correctly classifies that load as fresh, reload,
    // or back/forward-restored. history.state can't repeat that distinction on
    // this first call: a plain reload preserves the current entry's state
    // exactly as it was stamped before reloading, so treating "already stamped"
    // as "traversal" here would wrongly fire on a reload of a previously-visited
    // hash. Every later call this same app lifetime (a genuine hashchange, since
    // a real reload would end this lifetime) is unambiguous and checked normally.
    if (!hasCheckedInitialRoute) {
        hasCheckedInitialRoute = true
    } else if (history.state && history.state.__navVisited) {
        sessionStorage.setItem('browser-back-forward', 'true')
        return
    }
    try {
        window.history.replaceState({ ...(history.state || {}), __navVisited: true }, '', window.location.href)
    } catch (e) {
        // non-fatal - routing continues even if replaceState is unavailable
    }
}

async function handleRouteChange() {
     markBackNavigationIfTraversal()

     let hash = window.location.hash
     const previousHash = window.hashHistory[window.hashHistory.length - 1] || '#home'

     // Restore any preserved query string onto the URL before the
     // rest of the route change runs. `cached_query_params` is the
     // generic preservation channel seeded by index.tag's
     // preserveQueryParams (and the auth-error redirect in api.js)
     // for params that need to survive a redirect that strips the
     // hash query. Splicing them back onto the URL here means every
     // downstream consumer — currentQueryParams, component props,
     // page-level onMounted — just reads them from the URL like any
     // other route. New params added in the future "just work"
     // without needing a dedicated reader.
     //
     // Explicit URL query wins: we never overwrite query a user or
     // caller has already put on the hash.
     const preservedParams = typeof localStorage !== 'undefined'
         ? localStorage.getItem('cached_query_params')
         : null
     if (preservedParams) {
         if (!hash.includes('?')) {
             const route = hash && hash.startsWith('#') ? hash : `#${hash || 'login'}`
             const newHash = `${route}?${preservedParams}`
             window.history.replaceState(null, '', newHash)
             hash = newHash
         }
         localStorage.removeItem('cached_query_params')
     }
     
     // If hash changed, check for unsaved changes BEFORE proceeding
     if (hash !== previousHash && hasUnsavedChangesCallback) {
         try {
             const hasUnsaved = hasUnsavedChangesCallback()
             console.log('[Router] Unsaved changes check:', hasUnsaved, 'Current hash:', hash, 'Previous hash:', previousHash)
             
             if (hasUnsaved) {
                 const userConfirmed = confirm(locale.t('unsaved_changes_warning'))
                 console.log('[Router] User confirmed navigation:', userConfirmed)
                 
                 if (!userConfirmed) {
                     // User cancelled - restore the previous hash immediately (synchronously)
                     console.log('[Router] Restoring previous hash:', previousHash)
                     window.location.hash = previousHash
                     return
                 }
             }
         } catch (error) {
             console.error('[Router] Error checking unsaved changes:', error)
         }
     }
     
     // Only add to history if hash actually changed (and we passed the unsaved check)
     if (hash !== previousHash) {
         window.hashHistory.push(hash);
     }

     const queryParams = parseQueryParams(hash)
     const componentName = getComponentNameFromHash(hash)

    // Legacy alias: the standalone #/quota-management page was consolidated
    // into the Customer 360 Billing tab. Keep old bookmarks + deep-links
    // working by redirecting to the equivalent tab. Credits balance +
    // bundles now live at the top of the Usage pill (previously a
    // separate Credits pill), so land there. customer-360 resolves a
    // missing customerId to the caller's own, so no id is required here.
    if (componentName === 'quota-management-page') {
        window.location.replace('/#/customer-360?tab=billing&view=usage')
        return
    }

    // Legacy alias: the Content Console view was renamed Deep Search
    // (METALARK-1016) and its page/route renamed with it. Keep old
    // bookmarks + deep-links working.
    if (componentName === 'content-console-page') {
        window.location.replace('/#content/deep-search')
        return
    }

    // Check if this is a public route
    const isPublicRoute = PUBLIC_ROUTES.includes(componentName)

    // ── Ordering note ────────────────────────────────────────────────────
    // Every redirect/guard MUST run before we try to load the page bundle.
    // Previously the bundle loader ran first — so a CUSTOMER_ADMIN hitting
    // a SYSTEM_ADMIN-only URL (e.g. #/admin) threw "insufficient permissions
    // to load system-admin bundle" and the catch below fired the 404 page,
    // beating the role guard that would have redirected them to their own
    // Customer 360. Rule of thumb: 404 is the LAST thing the router does.
    // Each guard gets a chance to steer the user somewhere sensible first.
    // ──────────────────────────────────────────────────────────────────────

    // 1. Authentication — non-public routes require a real user context.
    if (!isPublicRoute) {
        try {
            // Import tools dynamically to avoid circular dependency
            const { tools } = await import('../services/tools.js')
            const userContext = await tools.getUserContext()

            // Check if user is actually authenticated
            // The fallback guest context has userId: null, so we check for that
            // A real authenticated user will have a userId
            const isAuthenticated = userContext && userContext.userId !== null

            if (!isAuthenticated) {
                console.log('[Router] Authentication required, redirecting to login', {
                    hasUserContext: !!userContext,
                    userId: userContext?.userId,
                    email: userContext?.email
                })
                // Use replace to avoid adding to history
                window.location.replace('/#login')
                return
            }

            console.log('[Router] User authenticated:', userContext.email)
        } catch (error) {
            console.error('[Router] Authentication check failed:', error)
            // On error, redirect to login
            window.location.replace('/#login')
            return
        }
    }

    // 1.5 Admin-tier landing redirect. Admin users (SYSTEM_ADMIN / CUSTOMER_ADMIN)
    //     land on an admin page — not on a workspace route — unless they are
    //     in an active access session (workspace-access or impersonation).
    //     The session token in localStorage (imp_ / wsa_ prefix) is the
    //     signal that they have already entered a workspace.
    //
    //     Applies to every workspace route: home, upload, content, skills,
    //     reports, export, migration. Any admin hitting these
    //     without a session gets bounced to the role-appropriate admin
    //     landing. Typed URLs, bookmarks, refreshes after logout, and the
    //     auto-redirect after login are all covered by one guard.
    //
    //     SYSTEM_ADMIN → #customers. CUSTOMER_ADMIN → their Customer 360.
    //     Non-admin callers fall through untouched.
    const WORKSPACE_ROUTES = [
        'home-page',
        'upload-page',
        'content-page',
        'skills-page',
        'skill-page',
        'collection-page',
        'reports-page',
        'report-preview-page',
        'export-page',
        'summary-page',
        'item-page',
        'deep-search-page',
        'migration-page',
    ]
    // 1.4 Auto-pause on return to admin / picker surfaces. When a caller
    //     is in a wsa_ session and navigates to an admin hub (customers,
    //     customer-360, admin) OR the workspace picker (select-workspace),
    //     treat that as a return-from-workspace action and pause the
    //     workspace session locally. Catches browser Back from a
    //     workspace route and manual typed navigation; the settings-cog
    //     "Return to Admin" button already does its own pause so this is
    //     idempotent for that path. The picker case matters for multi-
    //     workspace users — without the pause, hasActiveAccessSession
    //     stays true on the picker, the navbar workspace menu stays
    //     visible, and clicking Home routes them straight back into the
    //     workspace they just left without an explicit resume click.
    //
    //     Pause (not end) means the server session stays alive for resume
    //     via the Your Workspaces card or the picker grid. Only
    //     localStorage authToken, launch_token cookie, and Redux
    //     impersonation slice are cleared.
    //
    //     Only fires on an actual hashchange from a workspace route — not
    //     on first-load / refresh — so admins arriving fresh on an admin
    //     page with a session in localStorage (e.g. tab reopen) aren't
    //     surprise-paused before they can reach the card to Resume.
    const ADMIN_HUB_ROUTES = ['admin-page', 'customers-page', 'customer-360-page', 'select-workspace-page']
    const WORKSPACE_ROUTES_FOR_BACK_DETECTION = [
        'home-page', 'upload-page', 'content-page', 'skills-page', 'skill-page',
        'collection-page', 'reports-page', 'report-preview-page', 'export-page',
        'summary-page', 'item-page', 'deep-search-page', 'migration-page',
    ]
    if (
        hash !== previousHash
        && ADMIN_HUB_ROUTES.includes(componentName)
        && WORKSPACE_ROUTES_FOR_BACK_DETECTION.includes(getComponentNameFromHash(previousHash))
    ) {
        try {
            const tok = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null
            if (tok && tok.startsWith('wsa_')) {
                console.log('[Router] Return to admin hub from workspace route — pausing wsa_ session')

                // Play the workspace-menu fold-out animation before the
                // Redux dispatch so the cluster visibly slides out instead
                // of unmounting instantly when shouldShowWorkspaceMenu()
                // flips. The navbar's animation CSS lives on the cluster
                // element; we apply the .exiting class here and await its
                // duration so the page transition that follows feels
                // connected to the session exit. Skipped for
                // prefers-reduced-motion and when the cluster isn't
                // mounted.
                try {
                    const cluster = document.querySelector('.workspace-menu-cluster')
                    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    if (cluster && !reduced) {
                        cluster.classList.add('exiting')
                        await new Promise(resolve => setTimeout(resolve, 300))
                    }
                } catch (animError) {
                    console.warn('[Router] Menu exit animation skipped:', animError)
                }

                localStorage.removeItem('authToken')
                localStorage.removeItem('workspace_sliceId')
                localStorage.removeItem('workspace_sliceName')
                document.cookie = 'launch_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
                try {
                    const store = await loadStore()
                    store.dispatch({ type: 'clearImpersonation' })
                    store.dispatch({ type: 'setSliceId', payload: null })
                    // Clear the cached userContext so the target admin page
                    // re-fetches /auth/me without the Bearer — the cached
                    // entry was built under the workspace context.
                    const { tools } = await import('../services/tools.js')
                    if (typeof tools.clearUserContext === 'function') {
                        tools.clearUserContext()
                    }
                } catch (err) {
                    console.warn('[Router] Store cleanup during admin-return pause failed:', err)
                }
            }
        } catch (error) {
            console.error('[Router] Admin-return pause check failed:', error)
        }
    }

    if (WORKSPACE_ROUTES.includes(componentName)) {
        try {
            const { tools } = await import('../services/tools.js')
            const userContext = await tools.getUserContext()
            const role = userContext?.role
            const isAdminTier = role === 'SYSTEM_ADMIN' || role === 'CUSTOMER_ADMIN'

            let hasActiveSession = false
            try {
                const tok = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null
                hasActiveSession = !!tok && (tok.startsWith('imp_') || tok.startsWith('wsa_'))
            } catch {
                hasActiveSession = false
            }

            // METALARK-738: Admin-tier users whose role roster resolves to
            // exactly one native workspace get their sliceId stamped on the
            // session JWT at login (see backend resolveSessionSliceId).
            // /auth/me surfaces it as userContext.sliceId, which is the
            // signal that the admin has implicit single-workspace context
            // — they pass through to workspace routes without a wsa_ session.
            //
            // CUSTOMER_ADMIN landing rules:
            //   - Single workspace (hasImplicitWorkspaceContext === true):
            //     fall through to the workspace home, even on the post-login
            //     empty-hash redirect. The JWT already binds them to that
            //     slice, the picker would only have one card, and customer-360
            //     is one navbar click away.
            //   - Multi-workspace (hasImplicitWorkspaceContext === false):
            //     redirect to /#/select-workspace so they pick before landing.
            //
            // SYSTEM_ADMIN is unconditionally bounced to /#customers when
            // there's no active session — their admin landing is the global
            // customer list, not a workspace.
            const hasImplicitWorkspaceContext = !!userContext?.sliceId
            const isImplicitRoute = !hash || hash === '' || hash === '#'

            if (!hasActiveSession && (isImplicitRoute || !hasImplicitWorkspaceContext)) {
                if (role === 'SYSTEM_ADMIN') {
                    console.log(`[Router] Workspace guard: SYSTEM_ADMIN hit ${componentName} without a session -> #customers`)
                    window.location.replace('/#customers')
                    return
                }
                if (role === 'CUSTOMER_ADMIN' && userContext?.customerId && !hasImplicitWorkspaceContext) {
                    // Multi-workspace CUSTOMER_ADMIN: no sliceId on the
                    // session (resolveSessionSliceId only stamps for the
                    // single-workspace case). Send them to the workspace
                    // picker so they can pick before landing on home —
                    // this also covers the post-login "/" redirect, which
                    // becomes the implicit home-page route.
                    console.log(`[Router] Workspace guard: multi-workspace CUSTOMER_ADMIN hit ${componentName} without a session -> /#/select-workspace`)
                    window.location.replace('/#/select-workspace')
                    return
                }
                // Workspace-tier roles (TENANT_ADMIN and below) with no
                // implicit slice context have multiple direct workspace
                // assignments — same shape as multi-workspace
                // CUSTOMER_ADMIN, just without an admin landing to fall
                // back to. Send them to the picker. Skip when there IS an
                // implicit slice (single-workspace user post-login on the
                // empty-hash route) so they land on home with their lone
                // workspace context like before. Same fall-through covers
                // single-workspace CUSTOMER_ADMINs on the empty-hash route.
                if (!hasImplicitWorkspaceContext) {
                    console.log(`[Router] Workspace guard: ${role} hit ${componentName} without a workspace context -> /#/select-workspace`)
                    window.location.replace('/#/select-workspace')
                    return
                }
            }
        } catch (error) {
            console.error('[Router] Admin-tier guard check failed:', error)
            // Fall through — a broken guard shouldn't lock an admin out of the app.
        }
    }

    // 2. Role-based access for admin routes. Special case: CUSTOMER_ADMINs
    //    hitting #/admin get bounced to their own Customer 360 (historical
    //    behavior — admin.tag's onMounted used to handle this, but this
    //    guard now fires before onMounted can run). Other role mismatches
    //    fall through to the generic home redirect.
    if (ROLE_ROUTES[componentName]) {
        try {
            const { tools } = await import('../services/tools.js')
            const userContext = await tools.getUserContext()
            const role = userContext?.role
            const allowedRoles = ROLE_ROUTES[componentName]
            if (!role || !allowedRoles.includes(role)) {
                if (componentName === 'admin-page' && role === 'CUSTOMER_ADMIN' && userContext?.customerId) {
                    console.log(`[Router] CUSTOMER_ADMIN on #/admin — redirecting to their Customer 360`)
                    window.location.replace(`/#/customer-360?customerId=${encodeURIComponent(userContext.customerId)}`)
                    return
                }
                console.log(`[Router] Role ${role} cannot access ${componentName}; redirecting to home`)
                window.location.replace('/#home')
                return
            }
        } catch (error) {
            console.error('[Router] Role check failed:', error)
            window.location.replace('/#home')
            return
        }
    }

    // 3. Ensure required bundle is loaded. Runs AFTER the role guard so a
    //    user who can't access the bundle is redirected first instead of
    //    tripping the loader's permission check and landing on 404.
    try {
        const routeName = componentName.replace('-page', '')
        await bundleLoader.ensureBundleForRoute(routeName)
    } catch (error) {
        console.error('[Router] Failed to load required bundle:', error)
        // Last resort: the bundle loader refused for reasons the guards
        // above didn't catch (e.g. a new role-gated bundle we haven't
        // mapped in ROLE_ROUTES yet, or an actual network failure).
        await updateCurrentComponent('not-found-page', { error: 'bundle_load_failed' })
        return
    }

    // 4. Feature-flag gate for protected routes.
    if (FEATURE_FLAG_ROUTES[componentName]) {
        const requiredFlag = FEATURE_FLAG_ROUTES[componentName]
        const isEnabled = featureFlagService.isEnabledSync(requiredFlag)
        
        if (isEnabled === false) {
            console.log(`[Router] Route ${componentName} blocked by feature flag: ${requiredFlag}`)
            // Redirect to home with a message
            window.location.replace('/#home')
            return
        }
        
        console.log(`[Router] Feature flag ${requiredFlag} allows access to ${componentName}`)
    }
    
    await updateCurrentComponent(componentName, queryParams)
}

// Initialize routing system
export function initRouter() {
    // Handle hash changes (back/forward buttons and direct hash changes)
    window.addEventListener('hashchange', handleRouteChange)

    // In-app pushState changes don't fire `hashchange`; pages dispatch
    // `inapp-routechange` after their pushState writes so the pageview
    // tracker picks them up. See the comment block above
    // INAPP_ROUTE_CHANGE_EVENT for the full pattern.
    window.addEventListener(INAPP_ROUTE_CHANGE_EVENT, handleInappRouteChange)

    // Handle initial page load
    handleRouteChange()
    
    // Start background preloading of bundles based on user role
    // This happens asynchronously after router initialization
    setTimeout(() => {
        bundleLoader.preloadBundlesForRole().catch(error => {
            console.warn('[Router] Background bundle preload failed:', error)
        })
    }, 3000) // Wait 3 seconds after initial page load to start preloading
}

// Utility functions for navigation
export function navigateTo(hash) {
    // Update browser hash
    window.location.hash = hash
    // The hashchange event will automatically trigger handleRouteChange, whose
    // markBackNavigationIfTraversal() classifies this as a forward navigation
    // (the new entry's history.state starts null) - see that function's comment.
}

export function getCurrentRoute() {
    return window.location.hash
}

export function getCurrentComponent() {
    return currentComponent
}

export function getCurrentQueryParams() {
    return currentQueryParams
}